import React, { useEffect, useState } from 'react'
import backgroundImage from '../../images/bgd/bg-mobile-new.png'
import { useStoreActions, useStoreState } from '../../model/hooks'
import BigNumber from 'bignumber.js'
import { getCurrentRatio } from '../../services/amm_v3'
import { PoolOverview, TokenChartData, fetchRangeDataForPool, loadPoolOverviewData, loadTokenChartData } from './chartsApi'
import { calculateTickFromPrice, formatUSD, normalizeForTokens, numFormat } from '../../blockchainUtils/math'
import { getRangeArrayAroundTick } from './chartUtils'
import RangeSliderChart from '../PoolsPage/RangeSliderSelector/RangeSliderChart'
import Spinner from '../Spinner'
import { useNavigate } from 'react-router'
import { BackIcon } from '../../svgIcons/BackIcon'
import { PrecentChange } from './PrecentChange'
import { getIconUrl } from '../../services/static_data'
import { ChartsAppBar } from './ChartsAppBar'
import PrimaryButton from '../PrimaryButton'
import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { ChartTypes, ChartsContainer } from '../TokenPage/ChartsContainer'
import { getToken, removeLeadingZeros } from '../../model/tokens/helper'
import { GAtrack } from '../../services/ga'

export const PoolInfoPage = (props: { selectedPoolLiquidity: string }) => {
    const { selectedPoolLiquidity } = props
    const [currentTickInRange, setCurrentTickInRange] = useState(0)
    const [currentRatio, setCurrentRatio] = useState(0)
    const [poolChartData, setPoolChartData] = useState([] as TokenChartData[])
    const [allPoolChartData, setAllPoolChartData] = useState([] as TokenChartData[])
    const [poolOverview, setPoolOverview] = useState(undefined as unknown as PoolOverview)
    const [selectedChart, setSelectedChart] = useState('Volume' as ChartTypes)
    const [isRange1, setIsRange1] = useState<boolean | undefined>(undefined)

    const [liquidityRange, setLiquidityRange] = useState(
        [] as {
            tick: number
            liq: number
        }[]
    )
    useEffect(() => {
        if (!poolOverview) return
        const token1 = getToken('mainnet-alpha', poolOverview.token0!)
        const token2 = getToken('mainnet-alpha', poolOverview.token1!)
        if (isRange1 === undefined) {
            if (token1?.symbol === 'ETH') {
                setIsRange1(true)
            } else if (token2?.symbol === 'ETH') {
                setIsRange1(false)
            } else {
                setIsRange1(true)
            }
        }
    }, [poolOverview])

    const loadTokens = useStoreActions((actions) => actions.tokens.loadTokens)
    const [isReversed, setIsReversed] = useState(false)
    const navigate = useNavigate()
    const getTokenByAddress = useStoreState((state) => state.tokens.getToken)

    useEffect(() => {
        if (selectedPoolLiquidity) loadOverview()
    }, [selectedPoolLiquidity])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        loadTokens({ network: 'mainnet-alpha' })
    }, [])

    // useEffect(() => {
    //     if (data && data.pools) {
    //         const poolDataUpdated = data.pools.sort((a, b) => {
    //             if (a.tvl.gt(b.tvl)) return -1
    //             if (b.tvl.gt(a.tvl)) return 1
    //             return 0
    //         })
    //         setPoolsData(poolDataUpdated)
    //     }
    // }, [data])

    useEffect(() => {
        if (selectedPoolLiquidity && poolOverview && selectedChart === 'Liquidity') {
            loadPoolRangeData()
        }
    }, [selectedPoolLiquidity, poolOverview, selectedChart])

    useEffect(() => {
        if (selectedChart) onLoadTokenChartData()
    }, [selectedChart])

    async function onLoadTokenChartData() {
        if (!selectedPoolLiquidity && selectedChart !== 'Liquidity') return

        const poolAddress = removeLeadingZeros(selectedPoolLiquidity)

        const data = await loadTokenChartData(selectedChart.toLowerCase() as any, poolAddress!, 'pools')
        if (data && data.length > 0) {
            setPoolChartData(data)
            setAllPoolChartData(data)
        }
    }

    async function loadOverview() {
        const response = await loadPoolOverviewData(selectedPoolLiquidity)
        setPoolOverview(response!)
    }

    function onSelectChartType(type: ChartTypes) {
        setSelectedChart(type)
    }

    function onZoomChart(leftZoom: any, rightZoom: any) {
        if (leftZoom === rightZoom) return
        if (leftZoom === 'dataMin' && rightZoom === 'dataMax') return setPoolChartData(allPoolChartData)

        if (leftZoom === 'dataMin' || rightZoom === 'dataMax') return

        const start = leftZoom < rightZoom ? leftZoom : rightZoom
        const end = rightZoom > leftZoom ? rightZoom : leftZoom
        const oldData = [...poolChartData]

        const startPoint = oldData.findIndex((item: TokenChartData) => item.timestamp === start)
        const endPoint = oldData.findIndex((item: TokenChartData) => item.timestamp === end)

        setPoolChartData(oldData.slice(startPoint, endPoint))
    }

    async function loadPoolRangeData() {
        if (!poolOverview) return
        let tickAmount = 10
        const ratio: BigNumber = await getCurrentRatio('mainnet-alpha', selectedPoolLiquidity)
        const response = await fetchRangeDataForPool(selectedPoolLiquidity)
        const fee = poolOverview.fee
        if (!fee) return
        if (fee === 0.01) tickAmount = 2
        else if (fee === 0.05) tickAmount = 10
        else if (fee > 0.05 && fee <= 0.3) tickAmount = 60
        else tickAmount = 200

        const currentTick = calculateTickFromPrice(ratio ? ratio.toNumber() : 0, tickAmount)
        setCurrentRatio(ratio ? ratio.toNumber() : 0)
        const responseData = getRangeArrayAroundTick(response as any, currentTick, 200)
        setCurrentTickInRange(responseData.closestTickIndex)

        let token1 = getTokenByAddress('mainnet-alpha', removeLeadingZeros(poolOverview.token0))
        let token2 = getTokenByAddress('mainnet-alpha', removeLeadingZeros(poolOverview.token1))
        const isTokensReversed = BigNumber(token1?.address!).gt(token2?.address!)
        const token1Symbol = isTokensReversed ? token1!.symbol! : token2!.symbol!
        if (isTokensReversed || token1Symbol.toLowerCase() === 'eth') setIsReversed(true)

        setLiquidityRange(responseData.data)
    }

    function renderLoadingPoolLiquidity() {
        return (
            <div className='flex w-full h-[300px] items-center justify-center'>
                <Spinner />
            </div>
        )
    }

    function renderRangeSelector() {
        const token1 = getToken('mainnet-alpha', poolOverview.token0)
        const token2 = getToken('mainnet-alpha', poolOverview.token1)

        return (
            <div className='bg-[#0C2960] shadow-xl rounded-[8px] p-1 '>
                <span onClick={() => setIsRange1(true)} className={`cursor-pointer p-1 rounded-[8px] ${isRange1 ? 'bg-[#0AE8F9] text-[#0D0C1E]' : ''}`}>
                    {token1?.symbol}
                </span>
                <span onClick={() => setIsRange1(false)} className={`cursor-pointer p-1 rounded-[8px] ${!isRange1 ? 'bg-[#0AE8F9] text-[#0D0C1E]' : ''}`}>
                    {token2?.symbol}
                </span>
            </div>
        )
    }

    function renderCurrentRatioLine() {
        if (currentRatio === 0 || !poolOverview) return null

        let token0Symbol = getSymbol(poolOverview.token0)
        let token1Symbol = getSymbol(poolOverview.token1)
        const token0 = getTokenByAddress('mainnet-alpha', removeLeadingZeros(poolOverview.token0))
        const token1 = getTokenByAddress('mainnet-alpha', removeLeadingZeros(poolOverview.token1))

        const isTokensReversed = BigNumber(token0?.address!).gt(token1?.address!)
        if (isTokensReversed) {
            token0Symbol = getSymbol(poolOverview.token1)
            token1Symbol = getSymbol(poolOverview.token0)
        }

        let tokenAPriceInA = normalizeForTokens(BigNumber(currentRatio), token0!, token1!).toNumber()
        if (isTokensReversed) tokenAPriceInA = 1 / tokenAPriceInA

        return (
            <div className='text-white flex flex-col lg:text-auto items-center justify-center'>
                <div className='flex items-center mr-2'>
                    <span className='lg:text-[16px] text-[12px] mr-1'>Current Price</span>
                    <div className='w-[6px] h-[6px] rounded-full bg-[#FF5353] mr-2' />
                    {renderRangeSelector()}
                </div>
                <div>
                    {isRange1 ? (
                        <span className='text-[16px] lg:text-[22px]'>{`1 ${token0Symbol} = ${numFormat(
                            tokenAPriceInA.toString()
                        )} ${token1Symbol} | 1 ${token1Symbol} = ${numFormat((1 / tokenAPriceInA).toString())} ${token0Symbol}`}</span>
                    ) : null}
                    {!isRange1 ? (
                        <span className='text-[16px] lg:text-[22px]'>{`1 ${token1Symbol} = ${numFormat(
                            (1 / tokenAPriceInA).toString()
                        )} ${token0Symbol} | 1 ${token0Symbol} = ${numFormat(tokenAPriceInA.toString())} ${token1Symbol}`}</span>
                    ) : null}
                </div>
            </div>
        )
    }

    function renderBack() {
        return (
            <div className='w-full mt-[20px] lg:mt-[48px] flex items-center mb-2'>
                <div className='cursor-pointer text-[#0AE8F9] flex flex-row' onClick={() => navigate('/charts')}>
                    <BackIcon className='w-5 h-5 mr-2' />
                    <span>Back</span>
                </div>
            </div>
        )
    }

    function renderLoadingBasicInfo() {
        return (
            <div
                className='mb-2 lg:mb-0 w-full flex-1 lg:h-[120px] rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-6 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                {/* <span className='text-[12px]'>TVL</span> */}
                <div className='mt-4 flex flex-row lg:flex-col lg:items-start items-center'>
                    <Spinner />
                </div>
            </div>
        )
    }

    function renderTVL() {
        if (!poolOverview) return renderLoadingBasicInfo()

        const tvl = poolOverview.tvl ? poolOverview.tvl.toString() : '0'
        const percentageChange = poolOverview.tvl_change_24h ? BigNumber(poolOverview.tvl_change_24h.toString()) : new BigNumber(0)

        return (
            <div
                className='mb-2 lg:mb-0 lg:items-center w-full lg:w-auto lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <span className='lg:text-[16px] text-[12px]'>TVL</span>
                <div className='flex flex-col mt-2 flex-row items-center'>
                    <span className='lg:text-[24px] mr-2 text-[18px] font-bold'>{`$${formatUSD(Number(numFormat(tvl)))}`}</span>
                    {poolOverview.tvl_change_24h ? <PrecentChange fontSize={16} lgFontSize={14} value={percentageChange} /> : null}
                </div>
            </div>
        )
    }

    function renderVolume() {
        if (!poolOverview) return renderLoadingBasicInfo()

        return (
            <div
                className='w-full lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <span className='text-[12px] lg:text-[16px]'>Volume 24h</span>
                <div className='flex flex-col mt-2 items-center'>
                    <span className='lg:text-[24px] mr-2 text-[18px] font-bold'>{`$${formatUSD(Number(numFormat(poolOverview.volume_24h.toString())))}`}</span>
                    {poolOverview.volume_change_24h ? <PrecentChange fontSize={16} lgFontSize={14} value={BigNumber(poolOverview.volume_change_24h)} /> : null}
                </div>
            </div>
        )
    }

    function renderVolumeWeek() {
        if (!poolOverview) return renderLoadingBasicInfo()
        if (!poolOverview.tvl_change_7d) return null

        return (
            <div
                className='w-full lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] mr-4 px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <span className='text-[12px] lg:text-[16px]'>Volume 7d</span>
                <div className='flex flex-col mt-2 items-center'>
                    <span className='lg:text-[24px] mr-2 text-[18px] font-bold'>{`$${formatUSD(Number(numFormat(poolOverview.volume_7d.toString())))}`}</span>
                    {poolOverview.volume_change_7d ? <PrecentChange fontSize={16} lgFontSize={14} value={BigNumber(poolOverview.volume_change_7d)} /> : null}
                </div>
            </div>
        )
    }

    // function renderAccumulatedFees() {
    //     if (!poolOverview) return renderLoadingBasicInfo()

    //     // if (!poolsData || poolsData.length === 0) return renderLoadingBasicInfo()

    //     const selectedPool = poolsData.find((pool) => pool.pool_id === selectedPoolLiquidity)
    //     if (!selectedPool) return null
    //     const volume = selectedPool ? selectedPool.volume_24h.toString() : '0'
    //     const accumFees = (Number(volume) * selectedPool.pool_fee) / 100
    //     return (
    //         <div
    //             className='mr-4 w-full lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] px-4 py-4 rounded-2xl'
    //             style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
    //             <span className='lg:text-[16px] text-[12px]'>24h fees</span>
    //             <span className='lg:text-[24px] mt-2 mb-1 text-[18px] font-bold'>{`$${numFormat(accumFees.toString())}`}</span>
    //         </div>
    //     )
    // }

    function renderAPR() {
        if (!poolOverview) return renderLoadingBasicInfo()

        return (
            <div
                className='w-full mr-3 z-[1] h-full lg:mr-4 lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <div className='flex items-center justify-center lg:justify-auto'>
                    <span className='text-center lg:text-[16px] text-[12px]'>APR</span>
                    <div className='ml-2 group inline-flex'>
                        <HelptipIcon className='w-5 h-5' />
                        <span className='left-[auto] lg:left-[-20px] bottom-[85px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0  p-3 z-[1]'>
                            {`Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the pool's TVL.`}
                        </span>
                    </div>
                </div>
                <span className={`text-center  lg:text-[24px] mt-2 mb-1 text-[18px] font-bold text-[#68F2B0]`}>{`${numFormat(
                    (poolOverview.apr.incentive_apr ? poolOverview.apr.incentive_apr + poolOverview.apr.apr : poolOverview.apr.apr).toFixed(2)
                )}%`}</span>
                {poolOverview.apr.incentive_apr ? (
                    <span className='text-center lg:text-start text-[#0AE8F9] text-[12px] font-bold mt-1'>{`(${poolOverview.apr.incentive_apr.toFixed(2)}% DeFi Incentives)`}</span>
                ) : null}
            </div>
        )
    }

    function renderCLAPR() {
        if (!poolOverview) return renderLoadingBasicInfo()

        return (
            <div
                className='w-full mr-3 z-[1] h-full lg:mr-4 lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] px-4 py-4 rounded-2xl'
                style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
                <div className='flex items-center justify-center lg:justify-auto'>
                    <span className='text-center lg:text-[16px] text-[12px]'>CL APR</span>
                    <div className='ml-2 group inline-flex'>
                        <HelptipIcon className='w-5 h-5' />
                        {/* <span className="bg-red-400 text-white px-2 py-1">Button</span> */}
                        <span className='left-[auto] lg:left-[-20px] bottom-[85px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0  p-3 z-[1]'>
                            {`Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the TVL in a selected range.
                             \n Range for CL APR calculation: +/- ${poolOverview.apr && poolOverview.apr.percentage ? poolOverview.apr.percentage * 100 : 0}%`}
                            {/* {'7-day moving average'} */}
                        </span>
                    </div>
                </div>
                {/* <span className={`text-center  lg:text-[24px] mt-2 mb-1 text-[18px] font-bold text-[#68F2B0]`}>{`${numFormat(poolOverview.apr.incentive_apr ? (poolOverview.apr.incentive_apr + poolOverview.apr.apr_cl :poolOverview.apr.apr_cl).toFixed(2))}%`}</span> */}
                <span className={`text-center  lg:text-[24px] mt-2 mb-1 text-[18px] font-bold text-[#68F2B0]`}>{`${numFormat(
                    (poolOverview.apr.incentive_apr ? poolOverview.apr.incentive_apr + poolOverview.apr.apr_cl : poolOverview.apr.apr_cl).toFixed(2)
                )}%`}</span>
                {poolOverview.apr.incentive_apr ? (
                    <span className='text-center lg:text-start text-[#0AE8F9] text-[12px] font-bold mt-1'>{`(${poolOverview.apr.incentive_apr.toFixed(2)}% DeFi Incentives)`}</span>
                ) : null}
            </div>
        )
    }

    // function renderCLAPR() {
    //     if (!poolsData || poolsData.length === 0) return renderLoadingBasicInfo()

    //     const selectedPool = poolsData.find((pool) => pool.pool_id === selectedPoolLiquidity)
    //     if (!selectedPool) return null

    //     return (
    //         <div
    //             className='z-[10] w-full h-full lg:items-center lg:w-auto mb-2 lg:mb-0 lg:flex-1 rounded-[16px] flex flex-col border-[1px] border-[#0C3F6D] bg-[#0C1647] px-4 py-4 rounded-2xl'
    //             style={{ filter: 'drop-shadow(-1px 8px 43px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 11px rgba(0, 0, 0, 0.25))' }}>
    //             <div className='flex items-center justify-center lg:justify-auto'>
    //                 <span className='text-center lg:text-[16px] text-[12px]'>CL APR</span>
    //                 <div className='ml-2 group inline-flex'>
    //                     <HelptipIcon className='w-5 h-5' />
    //                     {/* <span className="bg-red-400 text-white px-2 py-1">Button</span> */}
    //                     <span className='right-[0px] lg:right-[-20px] bottom-[85px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0  p-3 z-[1]'>
    //                         {
    //                             'Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the TVL in a selected range. Hover over a pool for specific range details. '
    //                         }
    //                         {/* {'7-day moving average'} */}
    //                     </span>
    //                 </div>
    //             </div>
    //             <span className={`text-center  lg:text-[24px] mt-2 mb-1 text-[18px] font-bold text-[#68F2B0]`}>{`${numFormat(selectedPool.apr.apr_cl.toString())}%`}</span>
    //         </div>
    //     )
    // }

    function renderPoolInfo() {
        return (
            <div className='flex w-full lg:flex-row lg:w-auto lg:justify-start lg:mb-6 justify-between flex-col '>
                {renderTVL()}
                {renderVolume()}
                {renderVolumeWeek()}
                {/* {renderAccumulatedFees()} */}
                {renderAPR()}
                {renderCLAPR()}
                {/* <div style={{ flex: '2' }} className='mb-2 lg:mb-0 w-full h-full lg:items-center lg:w-auto flex-2 flex'> */}
                {/*renderCLAPR()*/}
                {/* </div> */}
            </div>
        )
    }

    function renderPoolIcons() {
        if (!poolOverview)
            return (
                <div className='flex items-center mb-[24px]'>
                    <Spinner />
                </div>
            )

        // const selectedPool = poolsData.find((pool) => pool.pool_id === selectedPoolLiquidity)
        // if (!selectedPool) return null
        const symbol1 = getSymbol(poolOverview.token0)
        const symbol2 = getSymbol(poolOverview.token1)
        return (
            <div className='flex items-center '>
                <img style={{}} src={getIconUrl('mainnet-alpha', poolOverview.token0)} alt='' width={24} height={24} />
                <img
                    style={{
                        marginRight: 8,
                        marginLeft: -8
                    }}
                    src={getIconUrl('mainnet-alpha', poolOverview.token1)}
                    alt=''
                    width={24}
                    height={24}
                />
                <div className='text-[24px] font-bold text-white ml-1 mr-4'>{`${symbol1} / ${symbol2}`}</div>
                <div className='text-[#0AE8F9] bg-[#0C3F6D] p-1 rounded-xl text-[16px]'>{poolOverview.fee + '%'}</div>
            </div>
        )
    }

    function renderPoolBasicInfo() {
        // const selectedPool = poolsData.find((pool) => pool.pool_id === selectedPoolLiquidity)
        if (!poolOverview)
            return (
                <div className='mb-4 w-full flex items-center justify-center'>
                    <Spinner />
                </div>
            )

        const token0 = getTokenByAddress('mainnet-alpha', removeLeadingZeros(poolOverview.token0))
        const token1 = getTokenByAddress('mainnet-alpha', removeLeadingZeros(poolOverview.token1))

        const areBothBluechip = token0?.is_bluechip && token1?.is_bluechip
        const isUnspported = !areBothBluechip

        return (
            <div className='flex lg:flex-row flex-col mb-[30px] justify-start lg:items-center w-full'>
                <div className='mr-4'>{renderPoolIcons()}</div>
                {isUnspported ? null : (
                    <PrimaryButton
                        className='mt-5 lg:mt-0 w-full lg:w-auto lg:pl-[24px] lg:pr-[24px]'
                        onClick={() => {
                            const ename = 'mint_add_position_from_charts'
                            GAtrack(ename, ename, ename)
                            navigate(`/create-position?token0=${token0!.address}&token1=${token1?.address!}&default_fee=${poolOverview.fee}`)
                        }}
                        size='lg'>
                        <span>{`+ Add Liquidity`}</span>
                    </PrimaryButton>
                )}
            </div>
        )
    }

    function renderCharts() {
        return (
            <ChartsContainer
                isZoomEnabled={selectedChart === 'Price' || selectedChart === 'TVL'}
                renderedChart={renderLiquidityChart()}
                options={['Liquidity', 'Volume', 'TVL']}
                chartData={poolChartData}
                selectedChart={selectedChart}
                setSelectedChart={onSelectChartType}
                onZoom={(left, right) => {
                    onZoomChart(left, right)
                }}
            />
        )
    }

    function getSymbol(address: string) {
        const token = getTokenByAddress('mainnet-alpha', removeLeadingZeros(address))
        return token?.symbol || ''
    }

    function renderLiquidityChart() {
        if (selectedChart !== 'Liquidity') return null

        return (
            <>
                {liquidityRange.length > 0 ? (
                    <>
                        <RangeSliderChart
                            token1Address={poolOverview.token0}
                            token2Address={poolOverview.token1}
                            token1Symbol={poolOverview ? getSymbol(isRange1 ? poolOverview.token0 : poolOverview.token1) : ''}
                            token0Symbol={poolOverview ? getSymbol(isRange1 ? poolOverview.token1 : poolOverview.token0) : ''}
                            currentTickInRange={currentTickInRange}
                            isMiminal={false}
                            chartHeight={280}
                            network='mainnet-alpha'
                            data={liquidityRange}
                            isReversed={!isRange1}
                            // isReversed={isReversed}
                        />
                        {renderCurrentRatioLine()}
                    </>
                ) : (
                    renderLoadingPoolLiquidity()
                )}
            </>
        )
    }

    return (
        <div
            className='min-h-[100vh] w-screen h-[1400px] flex flex-col pb-[60px]'
            // className='w-screen h-[1000px] lg:h-[100vh] flex flex-col pb-[60px]'
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}>
            {/* <Header onWalletConnected={() => {}} className={''} onWalletDisconnected={() => {}} selectedTab={''} /> */}
            {/* <ConnectWallet connected={() => {}} disconnected={() => {}} /> */}

            <div className='container flex w-full flex-col mx-auto items-center'>
                <ChartsAppBar />
            </div>
            <div className='container flex w-full flex-col mx-auto items-center p-4 lg:p-0'>
                {renderBack()}
                {renderPoolBasicInfo()}
                <div className='flex flex-col h-[420px] lg:h-[420px] w-full'>
                    {renderPoolInfo()}
                    {renderCharts()}
                </div>
            </div>
        </div>
    )
}
